/**
 * API layer per il sottosistema mail ordini.
 *
 * Espone:
 *  - Tipi allineati al backend (MailThreadRow, MailThreadRead, SupplierReplyPayload,
 *    SupplierReplyResult) — mirroring di modules/mail_send/schemas.py.
 *  - `useMailThread(orderRef)` — hook con anti-race (AbortController + requestSeq +
 *    mountedRef), copiato dal pattern di `useOrders` in features/ordini/api.ts.
 *  - `sendSupplierReply(orderRef, payload)` — POST /api/orders/{ref}/supplier-reply.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { apiAssetUrl, apiGet, apiPost, prettifyApiError } from "@/shared/api";

// ---------------------------------------------------------------------------
// Tipi — mirror di modules/mail_send/schemas.py
// ---------------------------------------------------------------------------

export type MailDirection = "outbound" | "inbound";

/** Singola riga nel thread mail di un ordine. Corrisponde a MailThreadRow (BE). */
export interface MailThreadRow {
  direction: MailDirection;
  subject: string;
  body: string;
  /** ISO 8601 string (datetime serializzato da Pydantic). */
  ts: string;
  attachment_names: string[];
  message_id: string | null;
  /** Status della riga (outbound: "sent"|"failed"|"imap_confirmed"|"sending"; inbound: null). */
  status?: string | null;
  /** Solo outbound: a chi e' partita la riga. «Riprova» la rimanda li'. */
  recipient?: string | null;
}

/** Risposta di GET /api/orders/{ref}/mail-thread. Corrisponde a MailThreadResponse (BE). */
export interface MailThreadRead {
  rows: MailThreadRow[];
}

/** Corpo della richiesta per POST /api/orders/{ref}/supplier-reply. */
export interface SupplierReplyPayload {
  body: string;
  packages_count?: number | null;
  recipient?: string | null;
  subject?: string | null;
}

/** Risposta di POST /api/orders/{ref}/supplier-reply. Corrisponde a SupplierReplyResponse (BE). */
export interface SupplierReplyResult {
  order_ref: string;
  message_id: string;
  thread_ref: string | null;
  label_name: string;
  in_reply_to: string | null;
  transport: string;
}

// ---------------------------------------------------------------------------
// useMailThread — hook con anti-race
// ---------------------------------------------------------------------------

export interface UseMailThreadResult {
  rows: MailThreadRow[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/**
 * Carica il thread mail dell'ordine identificato da `orderRef`.
 *
 * Anti-race: segue il pattern di `useOrders` (features/ordini/api.ts):
 *  - `requestSeq` (ref numerica) scarta risposte out-of-order.
 *  - `mountedRef` evita setState post-unmount (leak React 18).
 *  - `AbortController` annulla la fetch in-flight al cambio di `orderRef`
 *    o all'unmount del componente.
 */
export function useMailThread(orderRef: string): UseMailThreadResult {
  const [rows, setRows] = useState<MailThreadRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Contatore crescente: ogni chiamata ottiene il valore corrente; la risposta
  // viene scartata se seq < requestSeq.current (è arrivata fuori ordine).
  const requestSeq = useRef(0);

  // Guard post-unmount: React 18 logga warning se si fa setState su un
  // componente smontato. mountedRef è false dopo il cleanup dell'useEffect.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      const seq = ++requestSeq.current;
      setIsLoading(true);
      try {
        const response = await apiGet<MailThreadRead>(
          `/api/orders/${encodeURIComponent(orderRef)}/mail-thread`,
          { signal },
        );
        if (!mountedRef.current) return;
        if (seq === requestSeq.current) {
          setRows(response.rows);
          setError(null);
        }
      } catch (err) {
        if (signal?.aborted) return;
        if (!mountedRef.current) return;
        if (seq === requestSeq.current) {
          setError(prettifyApiError(err));
        }
      } finally {
        if (mountedRef.current && seq === requestSeq.current) {
          setIsLoading(false);
        }
      }
    },
    [orderRef],
  );

  // Fetch automatica al mount e al cambio di orderRef.
  // AbortController annulla la fetch in-flight se orderRef cambia o il
  // componente smonta prima della risposta.
  useEffect(() => {
    const ctrl = new AbortController();
    void refresh(ctrl.signal);
    return () => {
      ctrl.abort();
    };
  }, [refresh]);

  return { rows, isLoading, error, refresh };
}

// ---------------------------------------------------------------------------
// sendSupplierReply
// ---------------------------------------------------------------------------

/**
 * Invia una risposta al fornitore via mail.
 *
 * POST /api/orders/{ref}/supplier-reply
 * Il campo `recipient` è passato esplicito dal FE quando disponibile
 * (Task 14 si occuperà di popolare il mittente).
 */
export async function sendSupplierReply(
  orderRef: string,
  payload: SupplierReplyPayload,
): Promise<SupplierReplyResult> {
  return apiPost<SupplierReplyResult>(
    `/api/orders/${encodeURIComponent(orderRef)}/supplier-reply`,
    payload,
  );
}

// ---------------------------------------------------------------------------
// Tipi — preview/override (Task 1/3/4/6/7/8: mirror BE per i 3 flussi mail)
// ---------------------------------------------------------------------------

/** Override manuale destinatario/oggetto/corpo prima dell'invio effettivo. */
export interface MailOverride {
  recipient?: string | null;
  subject?: string | null;
  body?: string | null;
}

/** Bozza mail pre-invio, mostrata nel popup di conferma. */
export interface MailDraft {
  draft_key: string;
  order_refs: string[];
  recipient: string;
  subject: string;
  body: string;
  attachment_names: string[];
}

/** Risposta quando `preview: true` viene passato al posto dell'invio reale. */
export interface MailPreviewRead {
  preview: true;
  drafts: MailDraft[];
  errors?: { order_id: string; message: string }[];
}

/** Delta bozza→override (Task 11): manda solo i campi editati dal popup
 *  pre-invio, `undefined` = campo non toccato (il backend usa il default). */
export function draftToOverride(edited: MailDraft, original: MailDraft): MailOverride {
  return {
    recipient: edited.recipient !== original.recipient ? edited.recipient : undefined,
    subject: edited.subject !== original.subject ? edited.subject : undefined,
    body: edited.body !== original.body ? edited.body : undefined,
  };
}

/** Riga outbox (pagina Mail / storico invii). Corrisponde a OutboxRow (BE). */
export interface OutboxRow {
  id: number;
  order_ref: string;
  supplier_channel: string;
  recipient: string;
  subject: string;
  transport: string;
  status: string;
  error: string | null;
  sent_at: string;
  imap_confirmed_at: string | null;
  attachment_names: string[];
}

export interface OutboxCounts {
  by_supplier: Record<string, number>;
  pending: number;
  failed: number;
}

/** Risposta di GET /api/mail/outbox. */
export interface OutboxRead {
  rows: OutboxRow[];
  counts: OutboxCounts;
  total: number;
}

export interface OutboxFilters {
  supplier?: string;
  status?: string;
  q?: string;
  days?: number;
  limit?: number;
  offset?: number;
}

// ---------------------------------------------------------------------------
// Outbox + config (pagina Mail / gate popup)
// ---------------------------------------------------------------------------

export async function fetchOutbox(filters: OutboxFilters = {}): Promise<OutboxRead> {
  const params = new URLSearchParams();
  if (filters.supplier) params.set("supplier", filters.supplier);
  if (filters.status) params.set("status", filters.status);
  if (filters.q) params.set("q", filters.q);
  if (filters.days != null) params.set("days", String(filters.days));
  if (filters.limit != null) params.set("limit", String(filters.limit));
  if (filters.offset != null) params.set("offset", String(filters.offset));
  const qs = params.toString();
  return apiGet<OutboxRead>(`/api/mail/outbox${qs ? `?${qs}` : ""}`);
}

/** Backend mail attivo. ⚠️ Non è cosmetica: su ``thunderbird`` si apre una
 *  bozza che Davide invia a mano, su ``smtp`` la mail parte dal server. Chi
 *  manda mail DEVE distinguerli (popup di conferma, testo del feedback). */
export type MailBackend = "thunderbird" | "smtp";

let mailBackendPromise: Promise<MailBackend> | null = null;

/** Backend mail attivo, cache di modulo: il valore non cambia a runtime. */
export function getMailBackend(): Promise<MailBackend> {
  mailBackendPromise ??= apiGet<{ mail_backend: "thunderbird" | "smtp" }>("/api/mail/config")
    .then((r) => r.mail_backend)
    .catch((err: unknown) => {
      mailBackendPromise = null; // niente cache del fallimento: il prossimo tentativo rifà la fetch
      throw err;
    });
  return mailBackendPromise;
}

/** URL assoluto per il download di un allegato outbox (uso diretto in `<a href>`). */
export function outboxAttachmentUrl(rowId: number, index: number): string {
  return apiAssetUrl(`/api/mail/outbox/${rowId}/attachments/${index}`);
}
